import { useEffect, useState, useRef } from 'react';
import { Capacitor } from '@capacitor/core';
import { ScreenOrientation } from '@capacitor/screen-orientation';

import { debugLog, debugError, debugWarn, debugTable, debugGroup, debugGroupEnd } from '../utils/debugUtils';

/**
 * Custom hook to force landscape orientation for game components.
 *
 * Behavior:
 * - On native platforms (Capacitor): locks orientation via @capacitor/screen-orientation,
 *   which forces a real OS-level rotation (the Activity/UIViewController itself rotates) —
 *   no CSS trickery needed. If that call throws, falls back to the web path below.
 * - On web: Attempts to lock screen to landscape using the Web Screen Orientation API.
 *   If the API fails (not supported or not allowed — the common case on desktop browsers,
 *   where there's no real device orientation to lock), sets needsRotation=true when the
 *   viewport is in portrait, so the component can apply a CSS transform to rotate the view 90°.
 * - When lockOrientation=false: Does nothing (free orientation).
 *
 * @param {Function} onOrientationChange - Optional callback when orientation changes (receives boolean isLandscape)
 * @param {Object} options - Configuration options
 * @param {boolean} options.lockOrientation - Whether to attempt locking to landscape (default: false)
 *
 * @returns {Object} { needsRotation, isLandscape, isLocked }
 *   - needsRotation: true if CSS rotation fallback is needed (portrait + lock failed)
 *   - isLandscape: true if device is currently in landscape orientation
 *   - isLocked: true if orientation was successfully locked (native plugin or Web API)
 */
const useLandscapeOrientation = (onOrientationChange, options = {}) => {
    const { lockOrientation = false } = options;

    // State
    const [isLandscape, setIsLandscape] = useState(() => {
        // Initial check based on window dimensions
        return typeof window !== 'undefined' ? window.innerWidth > window.innerHeight : true;
    });
    const [needsRotation, setNeedsRotation] = useState(false);
    const [isLocked, setIsLocked] = useState(false);

    // Refs for cleanup
    const orientationLockedRef = useRef(false);
    const nativeLockedRef = useRef(false);
    const lockFailedRef = useRef(false);

    useEffect(() => {
        // If not locking orientation, do nothing
        if (!lockOrientation) {
            setNeedsRotation(false);
            setIsLocked(false);
            return;
        }

        // Function to check current orientation
        const checkOrientation = () => {
            const landscape = window.innerWidth > window.innerHeight;
            setIsLandscape(prevLandscape => {
                // Only call callback if orientation actually changed
                if (prevLandscape !== landscape && onOrientationChange) {
                    onOrientationChange(landscape);
                }
                return landscape;
            });
            
            // If lock failed and we're in portrait, we need CSS rotation
            if (lockFailedRef.current && !landscape) {
                setNeedsRotation(true);
            } else if (lockFailedRef.current && landscape) {
                // In landscape naturally, no rotation needed even if lock failed
                setNeedsRotation(false);
            }
        };

        // Try to lock orientation using the native plugin (real OS-level rotation, no CSS needed)
        const lockNative = async () => {
            checkOrientation();

            try {
                await ScreenOrientation.lock({ orientation: 'landscape' });
                nativeLockedRef.current = true;
                lockFailedRef.current = false;
                setIsLocked(true);
                setIsLandscape(true);
                setNeedsRotation(false);
                debugLog('Screen orientation locked to landscape via native plugin');
            } catch (error) {
                // Some devices/OS versions can reject the lock (e.g. tablets with
                // multi-window enabled) - fall back to the web path's CSS rotation.
                debugLog('Native orientation lock failed, falling back to Web API/CSS:', error.message);
                await lockOrientationAPI();
            }
        };

        // Try to lock orientation using Screen Orientation API
        const lockOrientationAPI = async () => {
            // First check current orientation
            checkOrientation();

            try {
                if (screen && screen.orientation && typeof screen.orientation.lock === 'function') {
                    await screen.orientation.lock('landscape');
                    orientationLockedRef.current = true;
                    lockFailedRef.current = false;
                    setIsLocked(true);
                    setIsLandscape(true);
                    setNeedsRotation(false);
                    debugLog('Screen orientation locked to landscape successfully');
                } else {
                    // API not available
                    debugLog('Screen Orientation API not supported, using CSS rotation fallback');
                    lockFailedRef.current = true;
                    setIsLocked(false);
                    // Check if we need rotation (portrait mode)
                    const inPortrait = window.innerWidth <= window.innerHeight;
                    setNeedsRotation(inPortrait);
                    
                    // Listen for orientation changes
                    window.addEventListener('resize', checkOrientation);
                    window.addEventListener('orientationchange', checkOrientation);
                }
            } catch (error) {
                // Lock failed - common in desktop browsers, non-fullscreen, or insecure contexts
                debugLog('Could not lock orientation:', error.message);
                lockFailedRef.current = true;
                setIsLocked(false);
                
                // Check if we need rotation (portrait mode)
                const inPortrait = window.innerWidth <= window.innerHeight;
                setNeedsRotation(inPortrait);
                
                // Listen for orientation changes
                window.addEventListener('resize', checkOrientation);
                window.addEventListener('orientationchange', checkOrientation);
            }
        };

        if (Capacitor.isNativePlatform()) {
            lockNative();
        } else {
            lockOrientationAPI();
        }

        // Cleanup function - unlock orientation when component unmounts
        return () => {
            window.removeEventListener('resize', checkOrientation);
            window.removeEventListener('orientationchange', checkOrientation);

            if (nativeLockedRef.current) {
                ScreenOrientation.unlock().then(() => {
                    debugLog('Native screen orientation unlocked');
                }).catch((error) => {
                    debugLog('Could not unlock native orientation:', error.message);
                });
            }

            if (orientationLockedRef.current && screen && screen.orientation && typeof screen.orientation.unlock === 'function') {
                try {
                    screen.orientation.unlock();
                    debugLog('Screen orientation unlocked');
                } catch (error) {
                    debugLog('Could not unlock orientation:', error.message);
                }
            }
            orientationLockedRef.current = false;
            nativeLockedRef.current = false;
            lockFailedRef.current = false;
        };
    }, [lockOrientation, onOrientationChange]);

    // Legacy compatibility: also return showRotateMessage (same as needsRotation) for gradual migration
    return { 
        needsRotation, 
        isLandscape, 
        isLocked,
        // Legacy alias - deprecated, use needsRotation instead
        showRotateMessage: needsRotation 
    };
};

export default useLandscapeOrientation;

